import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { ApiService } from 'src/app/services/api/api.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import { FormBuilder, Validators } from '@angular/forms';
import { SignupService } from 'src/app/services/signup/signup.service';
import fakerbr from 'faker-br';
import { MaskPipe } from 'ngx-mask';
import Utils from 'src/app/helpers/utils';
import { AgentsService } from 'src/app/services/agents/agents.service';
import { AuthService } from 'src/app/services/auth/auth.service';

@Component({
  selector: 'app-dashboard-settings-agents',
  templateUrl: './dashboard-settings-agents.component.html',
  styleUrls: ['./dashboard-settings-agents.component.scss'],
})
export class DashboardSettingsAgentsComponent implements OnInit {
  agentsForm = this.formBuilder.group({
    name: [null, [Validators.required]],
  });

  permissionObj: PermissionValues = {
    canAdd: false,
    canChange: false,
  };

  addAgents = false;
  agents: any[] = [];
  agent: any = undefined;
  visible_agents: any[] = [];

  agentsOptions = [];

  loading = false;

  // Carbon Warning Modal properties
  showDeleteModal = false;
  showMigrationModal = false;
  showSuccessModal = false;
  showErrorModal = false;
  agentToDelete: any = null;
  migrationOptions: { [Key: string]: string } = {};
  selectedMigrationRole: string = '';

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private api: ApiService,
    private toast: ToastService,
    private signupService: SignupService,
    private authService: AuthService,
    public agentsService: AgentsService
  ) {}

  ngOnInit(): void {
    this.getRoles();
    this.setData();
    this.setPermissions();
  }

  redirect(url: string) {
    this.router.navigate([url]);
  }

  async getRoles() {
    this.loading = true;

    try {
      const res = await this.api.get({
        route: 'api/registration/agent/',
        token: true,
      });

      this.agents = res.map((item: any) => {
        return {
          ...item,
          name: item.name,
          id: item.id,
        };
      });

      this.visible_agents = [...this.agents].splice(0, 10);

      this.agentsOptions = res.map((_agent: any) => {
        return {
          label: `${_agent.name}`,
          value: _agent.id,
        };
      });

      this.agentsService.data = this.agentsOptions;

      this.agentsService.onChange.emit();
    } catch (error) {
      console.warn(error);
      this.toast.show('error', 'Erro', 'Ocorreu um erro ao carregar os dados.');
    }
    this.loading = false;
  }

  async addAgent() {
    try {
      const values = this.agentsForm.value;

      const payload = {
        name: values.name,
      };

      const alreadyHaveAgent = this.agents.find(
        (_agent) => _agent.name === payload.name
      );

      if (alreadyHaveAgent) {
        this.toast.show('error', 'Erro', 'Já existe um agente com esse nome.');
        return;
      }

      const res = this.agent
        ? await this.api.put({
            route: `api/registration/agent/${this.agent.id}/`,
            token: true,
            body: payload,
          })
        : await this.api.post({
            route: 'api/registration/agent/',
            token: true,
            body: payload,
          });

      this.toast.show('info', 'Sucesso', 'Agente cadastrado com sucesso.');

      this.closeModal();

      this.getRoles();

      this.agent = undefined;

      this.agentsForm.reset();
    } catch (error) {
      this.toast.show(
        'error',
        'Erro',
        'Ocorreu um erro ao cadastrar o agente.'
      );
    }
  }

  addAgentsToggle(status: boolean) {
    this.addAgents = status;
  }

  closeModal() {
    this.agent = undefined;
    this.addAgents = false;
  }

  openDeleteModal(agent: any) {
    this.agentToDelete = agent;
    this.showDeleteModal = true;
  }

  closeDeleteModal() {
    this.showDeleteModal = false;
    this.agentToDelete = null;
  }

  async confirmDeleteAgent() {
    if (!this.agentToDelete) return;

    // Preparar opções de migração
    this.migrationOptions = {};
    this.agents
      .filter((_agent) => _agent.id !== this.agentToDelete.id)
      .forEach((_agent) => {
        this.migrationOptions[_agent.id] = _agent.name;
      });

    this.closeDeleteModal();
    this.showMigrationModal = true;
  }

  closeMigrationModal() {
    this.showMigrationModal = false;
    this.migrationOptions = {};
    this.selectedMigrationRole = '';
  }

  async confirmMigration(selectedRoleId: string) {
    if (!this.agentToDelete || !selectedRoleId) return;

    try {
      await this.api.delete({
        route: 'api/roles',
        token: true,
        params: {
          id: this.agentToDelete.id,
          role: selectedRoleId,
        },
      });

      this.agents = this.agents.filter(
        (agent) => agent.id !== this.agentToDelete.id
      );
      this.closeMigrationModal();
      this.showSuccessModal = true;
      this.getRoles();
    } catch (error) {
      this.closeMigrationModal();
      this.showErrorModal = true;
    }
  }

  closeSuccessModal() {
    this.showSuccessModal = false;
  }

  closeErrorModal() {
    this.showErrorModal = false;
  }

  // Modal methods
  getDeleteModalTitle() {
    return 'Tem certeza?';
  }

  getDeleteModalMessage() {
    return 'Você não poderá reverter isso!';
  }

  getDeleteConfirmText() {
    return 'Sim, apague!';
  }

  getDeleteCancelText() {
    return 'Cancelar';
  }

  getMigrationModalTitle() {
    return 'Atenção';
  }

  getMigrationModalMessage() {
    return 'Antes de continuar, selecione um papel para migrar os usuários';
  }

  getMigrationConfirmText() {
    return 'Prosseguir!';
  }

  getMigrationCancelText() {
    return 'Cancelar';
  }

  getSuccessModalTitle() {
    return 'Apagado!';
  }

  getSuccessModalMessage() {
    return 'Agente apagado com sucesso';
  }

  getSuccessConfirmText() {
    return 'OK';
  }

  getErrorModalTitle() {
    return 'Erro!';
  }

  getErrorModalMessage() {
    return 'Erro ao apagar agente';
  }

  getErrorConfirmText() {
    return 'OK';
  }

  getMigrationOptionsArray() {
    return Object.keys(this.migrationOptions).map((key) => ({
      label: this.migrationOptions[key],
      value: key,
    }));
  }

  setAgent(agent: any) {
    this.agentsForm.patchValue({
      ...agent,
    });
    this.agent = agent;
    this.addAgentsToggle(true);
  }

  isAdminField(slug: string) {
    if (slug) {
      return slug.includes('ADM');
    }
    return false;
  }

  setData() {
    this.signupService.fillFormEvent.subscribe(() => {
      this.agentsForm.patchValue({
        name: fakerbr.name.findName(),
        slug: Utils.convertToSlug(fakerbr.name.findName()),
        description: fakerbr.random.words(3),
        applicable: 'admin',
      });
    });
  }

  setPermissions() {
    const permissions = Object.keys(this.permissionObj).map(
      (key) => `de_can_${key.split('can').join('').toLowerCase()}_agents`
    );

    permissions.forEach((permission, idx) => {
      this.permissionObj[Object.keys(this.permissionObj)[idx]] =
        this.authService.verifyPermission(permission);
    });
  }
}
